import { FaroThumbnailSelector } from "@components/faro-thumbnail-selector";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { GroupId, ProjectId } from "@custom-types/base-types";
import { isSingleFileUploadResponse } from "@custom-types/file-upload-type-guards";
import {
  FileUploadTaskContext,
  UploadElementType,
} from "@custom-types/file-upload-types";
import { blue, Dialog, neutral, useToast } from "@flat-ui";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useFileUpload } from "@hooks/use-file-upload";
import { Tab, Tabs } from "@mui/material";
import { SphereDropzone } from "@src/components/common/sphere-dropzone/sphere-dropzone";
import {
  DEFAULT_TAB_UNDERLINE,
  TAB_WITH_NO_UNDERLINE,
} from "@styles/common-styles";
import {
  ChangeThumbnailTab,
  ThumbnailSubjectType,
} from "@utils/thumbnail-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PropsWithChildren, useState } from "react";

/** Represents a subject id, which can be either a project Id or group Id*/
type SubjectId = ProjectId | GroupId;

interface Props {
  setIsDialogOpen: (value: boolean) => void;

  /** ID of the item that thumbnail want to change  */
  subjectId: SubjectId;

  /** Type of the item that thumbnail want to change  */
  subjectType: ThumbnailSubjectType;

  /** Link to the existing project thumbnail */
  existingThumbnailUrl?: string;

  /**
   * Callback function invoked to update the thumbnail URL of the subject.
   *
   * @param thumbnailUrl - The new URL of the thumbnail to be set for the subject.
   * @returns A promise that resolves when the thumbnail update operation is successfully
   */
  onUpdateThumbnail: (thumbnailUrl: string) => Promise<void>;

  /** The name of the event to track when the thumbnail is changed. */
  eventName: string;
}

interface UpdateThumbnailProps {
  /** URL of the new thumbnail image */
  thumbnailUrl: string;

  /** Source of the thumbnail change: 'From Computer' or 'From Gallery' */
  location: string;
}

/** Renders a dialog to change the thumbnail */
export function FaroChangeThumbnailDialog({
  setIsDialogOpen,
  subjectId,
  subjectType,
  existingThumbnailUrl,
  onUpdateThumbnail,
  eventName,
}: PropsWithChildren<Props>): JSX.Element {
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { uploadSingleFile } = useFileUpload();
  const { trackEvent } = useTrackEvent();
  const companyId = useCompanyId();

  const [isConfirmDisabled, setIsConfirmDisabled] = useState<boolean>(true);
  const [selectedThumbnail, setSelectedThumbnail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<ChangeThumbnailTab>(
    ChangeThumbnailTab.computer
  );

  /**
   * The context for the file upload depending on subject type.
   * When a file is uploaded the first step is to request a signed URL from the Core API.
   * This request requires a single context ID: companyId, projectId or userId.
   * If the request includes more than one context ID, the Core API will return an error.
   * For project thumbnails, the context is the projectId.
   * For group thumbnails, the context is the companyId. The groupId can be included in this map
   * but it won't be part of the signed URL request to the Core API.
   */
  const uploadContextMap: Record<ThumbnailSubjectType, FileUploadTaskContext> =
    {
      [ThumbnailSubjectType.project]: {
        uploadElementType: UploadElementType.project,
        projectId: subjectId,
      },
      [ThumbnailSubjectType.group]: {
        uploadElementType: UploadElementType.group,
        companyId,
        groupId: subjectId,
      },
    };

  /** Triggers when an image is selected from gallery tab */
  function onSelectImageFromGallery(imageUrl: string): void {
    setIsConfirmDisabled(false);
    setSelectedThumbnail(imageUrl);
  }

  /**
   * Update thumbnail from gallery or by uploading from computer
   *
   * @param thumbnailUrl The url of the new image
   * @param location From where the change is triggered
   */
  async function updateThumbnail({
    thumbnailUrl,
    location,
  }: UpdateThumbnailProps): Promise<void> {
    setIsLoading(true);
    trackEvent({
      name: eventName,
      props: { location },
    });

    try {
      await onUpdateThumbnail(thumbnailUrl);
      openToast({
        title: "Thumbnail image changed.",
      });
    } catch (error) {
      // Handle error directly here in order to show specific info about the field that failed: thumbnailUrl
      // Otherwise the error will be handled by the error slice, but that will show a generic message without details
      handleErrorWithToast({
        id: `updateThumbnail-${Date.now().toString()}`,
        title: "Could not change thumbnail image",
        error,
      });
    } finally {
      setIsLoading(false);
      setIsConfirmDisabled(true);
      setIsDialogOpen(false);
      setIsUploading(false);
    }
  }

  /** Uploads a file which is selected from gallery */
  async function uploadSelectedThumbnail(): Promise<void> {
    setIsLoading(true);

    // Load the file as File object
    const fileResponse = await fetch(selectedThumbnail).then((res) =>
      res.arrayBuffer()
    );
    const fileName = selectedThumbnail.split("/").pop() ?? "thumbnail.svg";
    const file = new File([fileResponse], fileName, { type: "image/svg+xml" });

    // Upload the gallery file
    await uploadSingleFile({
      file,
      onUploadStart: () => {
        // Not showing progress here
      },
      onUploadProgress: () => {
        // Not showing progress here
      },
      onUploadComplete: (uploadedResponse) =>
        updateThumbnail({
          thumbnailUrl: uploadedResponse,
          location: "From Gallery",
        }),

      context: uploadContextMap[subjectType],
    });
  }

  return (
    <Dialog
      title="Change Thumbnail"
      open={true}
      confirmText={isLoading ? "Applying Image" : "Apply Image"}
      onConfirm={
        selectedTab === ChangeThumbnailTab.computer
          ? undefined
          : () => uploadSelectedThumbnail()
      }
      isConfirmDisabled={isConfirmDisabled}
      onClose={
        selectedTab === ChangeThumbnailTab.computer
          ? () => setIsDialogOpen(false)
          : undefined
      }
      onCancel={
        selectedTab === ChangeThumbnailTab.computer
          ? undefined
          : () => setIsDialogOpen(false)
      }
      showSpinner={isLoading}
    >
      <Tabs
        value={selectedTab}
        scrollButtons
        sx={{
          boxShadow: {
            xs: TAB_WITH_NO_UNDERLINE,
            md: DEFAULT_TAB_UNDERLINE,
          },
        }}
        slotProps={{
          indicator: {
            sx: {
              backgroundColor: blue[500],
              height: "3px",
            },
          },
        }}
      >
        {Object.values(ChangeThumbnailTab).map((tab) => (
          <Tab
            disableRipple
            draggable={false}
            key={tab}
            label={tab}
            value={tab}
            onClick={() => setSelectedTab(tab)}
            sx={{
              padding: 0,
              fontSize: "14px",
              letterSpacing: "-0.2px",
              marginRight: "20px",
              textTransform: "none",
              minWidth: "30px",
              fontWeight: selectedTab === tab ? "bold" : "lighter",
              "&.MuiTab-root": {
                color: selectedTab === tab ? blue[500] : neutral[800],
              },
              "&:hover": {
                color: blue[500],
                borderBottom: `1px solid ${blue[500]}`,
                // Add a top padding to avoid the tab content from jumping
                paddingTop: "1px",
              },
            }}
          />
        ))}
      </Tabs>
      {selectedTab === ChangeThumbnailTab.gallery ? (
        <FaroThumbnailSelector
          subjectType={subjectType}
          selectedThumbnail={selectedThumbnail}
          onSelectImage={onSelectImageFromGallery}
        />
      ) : (
        <SphereDropzone
          existingImageUrl={existingThumbnailUrl}
          isLoading={isUploading}
          setIsLoading={setIsUploading}
          shouldCompressImage={true}
          onUploadComplete={(uploadedResponse) => {
            if (isSingleFileUploadResponse(uploadedResponse)) {
              void updateThumbnail({
                thumbnailUrl: uploadedResponse,
                location: "From Computer",
              });
            }
          }}
          context={uploadContextMap[subjectType]}
        />
      )}
    </Dialog>
  );
}
